<template>
<div>
    <!-- HERO -->
    <section class="pt-12 pt-md-14 mt-n11 bg-primary">
        <div class="shape shape-blur-3 svg-shim text-white">
            <svg viewBox="0 0 1738 487" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 0h1420.92s713.43 457.505 0 485.868C707.502 514.231 0 0 0 0z" fill="url(#paint0_linear)"></path>
                <defs>
                    <linearGradient id="paint0_linear" x1="0" y1="0" x2="1049.98" y2="912.68" gradientUnits="userSpaceOnUse">
                        <stop stop-color="currentColor" stop-opacity=".075"></stop>
                        <stop offset="1" stop-color="currentColor" stop-opacity="0"></stop>
                    </linearGradient>
                </defs>
            </svg>
        </div>
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-8 text-center">
                    <h1 class="display-2 fw-bold text-white">Documentation</h1>
                    <p class="lead text-white-80 mb-6">
                        Complete guides for all {{ $state.siteName }} products. Learn how to integrate our APIs,
                        use MCP with AI tools, and work with Instagram datasets.
                    </p>
                </div>
            </div>
        </div>
    </section>

    <!-- PRODUCT CARDS -->
    <section class="py-8 py-md-11 mt-n8 mt-md-n12">
        <div class="container">
            <div class="row gx-4">
                <!-- Cache API -->
                <div class="col-12 col-md-6 col-lg-3 mb-4">
                    <div class="card shadow-light-lg h-100 lift">
                        <div class="card-body">
                            <div class="icon text-primary mb-3">
                                <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z" fill="currentColor"/>
                                </svg>
                            </div>
                            <h4 class="fw-bold">Cache API</h4>
                            <p class="text-muted mb-4">
                                REST API for accessing cached Instagram data. Get user profiles, posts, stories, and more.
                            </p>
                            <router-link :to="{path: '/docs/cache-api'}" class="btn btn-sm btn-primary-soft">
                                Read docs <i class="fe fe-arrow-right ms-2"></i>
                            </router-link>
                        </div>
                    </div>
                </div>

                <!-- MCP Server -->
                <div class="col-12 col-md-6 col-lg-3 mb-4">
                    <div class="card shadow-light-lg h-100 lift">
                        <div class="card-body">
                            <div class="icon text-primary mb-3">
                                <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
                                </svg>
                            </div>
                            <h4 class="fw-bold">MCP Server</h4>
                            <p class="text-muted mb-4">
                                Model Context Protocol for AI integration. Connect Claude Desktop or Claude.ai to Instagram data.
                            </p>
                            <router-link :to="{path: '/docs/mcp'}" class="btn btn-sm btn-primary-soft">
                                Read docs <i class="fe fe-arrow-right ms-2"></i>
                            </router-link>
                        </div>
                    </div>
                </div>

                <!-- Datasets -->
                <div class="col-12 col-md-6 col-lg-3 mb-4">
                    <div class="card shadow-light-lg h-100 lift">
                        <div class="card-body">
                            <div class="icon text-primary mb-3">
                                <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" fill="currentColor"/>
                                </svg>
                            </div>
                            <h4 class="fw-bold">Datasets</h4>
                            <p class="text-muted mb-4">
                                Pre-built datasets with millions of Instagram profiles and posts. Ready for download in CSV format.
                            </p>
                            <router-link :to="{path: '/docs/datasets'}" class="btn btn-sm btn-primary-soft">
                                Read docs <i class="fe fe-arrow-right ms-2"></i>
                            </router-link>
                        </div>
                    </div>
                </div>

                <!-- Dataset Builder -->
                <div class="col-12 col-md-6 col-lg-3 mb-4">
                    <div class="card shadow-light-lg h-100 lift">
                        <div class="card-body">
                            <div class="icon text-primary mb-3">
                                <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z" fill="currentColor"/>
                                </svg>
                            </div>
                            <h4 class="fw-bold">Dataset Builder</h4>
                            <p class="text-muted mb-4">
                                Build custom datasets by keywords, hashtags, or locations. Collect fresh data tailored to your needs.
                            </p>
                            <router-link :to="{path: '/docs/dataset-builder'}" class="btn btn-sm btn-primary-soft">
                                Read docs <i class="fe fe-arrow-right ms-2"></i>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- QUICK LINKS -->
    <section class="py-8 py-md-11 bg-gray-200">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-8 text-center">
                    <h2 class="fw-bold">Quick Start Guides</h2>
                    <p class="text-muted mb-6">
                        Get started quickly with our most popular features
                    </p>
                </div>
            </div>
            <div class="row">
                <div class="col-12 col-md-6 col-lg-4 mb-4">
                    <div class="card bg-dark border-0 h-100">
                        <div class="card-body">
                            <h5 class="text-white">Authentication</h5>
                            <p class="text-muted small mb-3">Learn how to get your API token and authenticate requests</p>
                            <router-link :to="{path: '/docs/cache-api', hash: '#authentication'}" class="text-primary small">
                                View guide <i class="fe fe-arrow-right"></i>
                            </router-link>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4 mb-4">
                    <div class="card bg-dark border-0 h-100">
                        <div class="card-body">
                            <h5 class="text-white">Connect Claude to MCP</h5>
                            <p class="text-muted small mb-3">Set up Claude Desktop or Claude.ai with our MCP server</p>
                            <router-link :to="{path: '/docs/mcp', hash: '#setup'}" class="text-primary small">
                                View guide <i class="fe fe-arrow-right"></i>
                            </router-link>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4 mb-4">
                    <div class="card bg-dark border-0 h-100">
                        <div class="card-body">
                            <h5 class="text-white">Download Your First Dataset</h5>
                            <p class="text-muted small mb-3">Purchase and download a ready-made Instagram dataset</p>
                            <router-link :to="{path: '/docs/datasets', hash: '#purchase'}" class="text-primary small">
                                View guide <i class="fe fe-arrow-right"></i>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- API OVERVIEW -->
    <section class="py-8 py-md-11">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-8 text-center mb-6">
                    <h2 class="fw-bold">API Overview</h2>
                    <p class="text-muted">
                        All {{ $state.siteName }} products use a unified authentication system
                    </p>
                </div>
            </div>
            <div class="row">
                <div class="col-12 col-lg-6 mb-4">
                    <div class="docs-diagram">
                        <pre class="mb-0 text-white-80">
┌─────────────────────────────────────────────────────┐
│                   Your Application                   │
└─────────────────────┬───────────────────────────────┘
                      │ API Token (x-access-key)
                      ▼
┌─────────────────────────────────────────────────────┐
│              {{ $state.siteName }} API Gateway                    │
├─────────────────────┬───────────────────────────────┤
│                     │                               │
│  ┌─────────────┐    │    ┌─────────────────────┐   │
│  │  Cache API  │    │    │    MCP Server       │   │
│  │   /v1/*     │    │    │  mcp.datalikers.com │   │
│  └──────┬──────┘    │    └──────────┬──────────┘   │
│         │           │               │              │
│         ▼           │               ▼              │
│  ┌─────────────┐    │    ┌─────────────────────┐   │
│  │  Database   │◄───┼───►│   Dataset Builder   │   │
│  │  (Storage)  │    │    │     (Workers)       │   │
│  └─────────────┘    │    └─────────────────────┘   │
│                     │                               │
└─────────────────────┴───────────────────────────────┘</pre>
                    </div>
                </div>
                <div class="col-12 col-lg-6 mb-4">
                    <h4 class="mb-4">Key Concepts</h4>
                    <dl>
                        <dt class="text-primary-desat">API Token</dt>
                        <dd class="text-muted mb-3">
                            Your unique access key for all API requests. Create tokens in your
                            <router-link :to="{path: '/tokens'}">Console</router-link>.
                        </dd>

                        <dt class="text-primary-desat">Rate Limits</dt>
                        <dd class="text-muted mb-3">
                            Request limits depend on your plan. Check your current limits on the
                            <router-link :to="{path: '/pricing'}">Pricing</router-link> page.
                        </dd>

                        <dt class="text-primary-desat">Data Freshness</dt>
                        <dd class="text-muted mb-3">
                            Cache API returns cached data (up to 7 days old). Dataset Builder collects fresh data on demand.
                        </dd>

                        <dt class="text-primary-desat">Pricing</dt>
                        <dd class="text-muted">
                            Pay per request for Cache API/MCP, or per row for Datasets. See
                            <router-link :to="{path: '/pricing'}">Pricing</router-link> for details.
                        </dd>
                    </dl>
                </div>
            </div>
        </div>
    </section>

    <!-- CTA -->
    <section class="py-8 py-md-11 bg-primary">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-8 text-center">
                    <h2 class="fw-bold text-white">Ready to get started?</h2>
                    <p class="text-white-80 mb-6">
                        Create a free account and get 100 API requests to try our services
                    </p>
                    <router-link :to="{path: '/registration'}" class="btn btn-white lift" v-if="!$state.context.id">
                        Create Free Account
                    </router-link>
                    <router-link :to="{path: '/dashboard'}" class="btn btn-white lift" v-else>
                        Go to Console
                    </router-link>
                </div>
            </div>
        </div>
    </section>
</div>
</template>

<script>
export default {
    created() {
        this.$methods.buildTitle('Documentation')
    }
}
</script>
